import { useEffect, useRef, useState } from "react";
import {
  ConfirmDeleteModal,
  useBatchSelection,
} from "@components/CatalogIndex";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import { useRecommendationsList } from "./api/useRecommendationsList";
import { useRecommendationOperands } from "./api/useRecommendationOperands";
import { useItemPickers } from "./api/useItemPickers";
import { useRecommendationMutations } from "./api/useRecommendationMutations";
import { RecommendationsList } from "./RecommendationsList";
import { RecommendationForm } from "./RecommendationForm";
import { reorderFlat } from "./reorder";
import {
  emptyRecommendationForm,
  type ItemOption,
  type Recommendation,
  type RecommendationFormValues,
} from "./types";

export interface RecommendationsTabProps {
  questionnaireId: string;
}

const toFormValues = (r: Recommendation): RecommendationFormValues => ({
  quantityValue: r.quantityValue,
  itemId: r.itemId,
  itemType: r.itemType,
  conditionType: r.conditionType,
  conditionalValue: r.conditionalValue,
  comparisonValue: r.comparisonValue,
  comparisonValueType: r.comparisonValueType,
  refinements: r.refinements.reduce<Record<string, string>>((acc, rf) => {
    acc[rf.taskId] = rf.quantityValue;
    return acc;
  }, {}),
});

export const RecommendationsTab = ({
  questionnaireId,
}: RecommendationsTabProps): JSX.Element => {
  const recommendations = useRecommendationsList(questionnaireId);
  const { operands } = useRecommendationOperands(questionnaireId);
  const { services, governances, products } = useItemPickers();
  const mutations = useRecommendationMutations(questionnaireId);
  const sel = useBatchSelection();

  // Optimistic local order for drag-reorder.
  const rowsRef = useRef(recommendations.rows);
  rowsRef.current = recommendations.rows;
  const [localRows, setLocalRows] = useState<Recommendation[]>(
    () => recommendations.rows
  );
  useEffect(() => {
    if (!recommendations.isFetching) setLocalRows(rowsRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recommendations.isFetching]);

  const [view, setView] = useState<
    { mode: "list" } | { mode: "form"; editing?: Recommendation }
  >({ mode: "list" });
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [toast, setToast] = useState<{
    variant: "success" | "danger";
    text: string;
  } | null>(null);

  const nameOf = (list: ItemOption[], id: string) =>
    list.find((o) => o.id === id)?.name ?? "";
  const itemName = (r: Recommendation): string => {
    const list =
      r.itemType === "Task"
        ? services
        : r.itemType === "Governance"
        ? governances
        : products;
    return nameOf(list, r.itemId);
  };

  const saveRecommendation = async (vals: RecommendationFormValues) => {
    try {
      if (view.mode === "form" && view.editing) {
        await mutations.update(view.editing.id, vals);
      } else {
        await mutations.create(vals);
      }
      setView({ mode: "list" });
      recommendations.refetch();
    } catch {
      setToast({ variant: "danger", text: "Could not save recommendation." });
    }
  };

  const handleReorder = async (activeId: string, overId: string) => {
    const { next, changed } = reorderFlat(localRows, activeId, overId);
    if (!changed.length) return;
    setLocalRows(next);
    try {
      for (const r of changed) await mutations.reorder(r.id, r.position);
    } catch {
      setToast({
        variant: "danger",
        text: "Could not save recommendation order.",
      });
    }
    recommendations.refetch();
  };

  const confirmBatchDelete = async () => {
    setConfirmDelete(false);
    const ids = [...sel.selectedIds];
    let failures = 0;
    for (const id of ids) {
      try {
        await mutations.remove(id);
      } catch {
        failures += 1;
      }
    }
    if (failures > 0) {
      setToast({
        variant: "danger",
        text: `Failed to delete ${failures} of ${ids.length} recommendation(s).`,
      });
    }
    sel.clear();
    recommendations.refetch();
  };

  if (recommendations.isLoading) return <ScopeStackSpinner />;

  return (
    <div className="recommendations-tab p-2">
      {view.mode === "form" ? (
        <RecommendationForm
          mode={view.editing ? "edit" : "create"}
          initialValues={
            view.editing ? toFormValues(view.editing) : emptyRecommendationForm
          }
          operands={operands}
          services={services}
          governances={governances}
          products={products}
          onCancel={() => setView({ mode: "list" })}
          onSave={saveRecommendation}
        />
      ) : (
        <RecommendationsList
          rows={localRows}
          selectedIds={sel.selectedIds}
          setSelectedIds={sel.setSelectedIds}
          onAdd={() => setView({ mode: "form" })}
          onEdit={(r) => setView({ mode: "form", editing: r })}
          onBatchDelete={() => setConfirmDelete(true)}
          onReorder={handleReorder}
          itemName={itemName}
        />
      )}

      <ConfirmDeleteModal
        open={confirmDelete}
        title="Delete recommendations?"
        message={`Delete ${sel.selectedIds.length} selected recommendation(s)?`}
        confirmLabel="Delete"
        onConfirm={confirmBatchDelete}
        onCancel={() => setConfirmDelete(false)}
      />

      {toast && (
        <ToastAlert
          variant={toast.variant}
          text={toast.text}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default RecommendationsTab;
